/**
 * Observability bootstrap — Sentry + GA4 + Microsoft Clarity.
 *
 * Toate sunt condiționate pe env vars la build time. Daca env nu e setata,
 * codul devine no-op (zero impact pe pagini). Valorile se pun in
 * frontend/.env.production (versionat) — NU in Vercel env, de unde nu se pot
 * decripta la build local si ar ajunge goale (vezi tools/vercel_env.sh):
 *   REACT_APP_SENTRY_DSN
 *   REACT_APP_GA_ID  (ex. G-ABC123XYZ)
 *   REACT_APP_CLARITY_ID  (ex. abcdef123)
 *
 * IMPORTANT — Sentry SDK e LAZY LOADED via dynamic import dupa idle/load
 * ca sa nu adauge ~60KB la main bundle. Trade-off: erorile aparute in primele
 * ~1-2 secunde dupa load nu sunt prinse, dar majoritatea erorilor reale apar
 * in interactiunile user-ului dupa primul paint.
 */

/** Domeniile pe care avem voie sa raportam trafic real. */
const PRODUCTION_HOSTS = ['www.privatebrands.ro', 'privatebrands.ro'];

/**
 * GA4 si Clarity pornesc DOAR pe domeniul de productie.
 *
 * 2026-08-13 — ID-urile stau acum in frontend/.env.production (versionat), deci
 * ajung si in build-urile de Preview pe care Vercel le face automat la fiecare
 * PR, si in orice build local de productie. Fara poarta asta, click-urile de pe
 * `*-git-*.vercel.app` sau de pe dev ar intra in acelasi property ca traficul
 * real si ar strica exact cifrele pentru care am reparat analytics-ul.
 */
export function isAnalyticsHost(hostname) {
  return PRODUCTION_HOSTS.includes(hostname);
}

export function initObservability() {
  const onProductionHost =
    typeof window !== 'undefined' && isAnalyticsHost(window.location.hostname);

  // ── Sentry — lazy load AFTER first paint (idle callback) ────
  const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
  if (SENTRY_DSN) {
    const initSentry = async () => {
      const SentryModule = await import('@sentry/react');
      SentryModule.init({
        dsn: SENTRY_DSN,
        // Erorile de pe Preview/dev raman colectate (sunt utile), dar etichetate
        // separat, ca sa nu se amestece cu incidentele reale din productie.
        environment: onProductionHost ? 'production' : 'preview',
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'Non-Error promise rejection captured',
        ],
      });
      if (typeof window !== 'undefined') window.Sentry = SentryModule;
      if (process.env.NODE_ENV !== 'production') console.log('[Observability] Sentry initialized (lazy)');
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(initSentry, { timeout: 3000 });
      } else {
        setTimeout(initSentry, 1500);
      }
    }
  }

  // ── GA4 + Microsoft Clarity — DEFER pana dupa load event ─────
  // Reduce Total Blocking Time la initial render.
  // Sentry ramane sincron (e mic + critical pentru error tracking).
  const initAnalyticsAfterLoad = () => {
    // Google Analytics 4
    const GA_ID = process.env.REACT_APP_GA_ID;
    if (GA_ID && onProductionHost) {
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(gaScript);

      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', GA_ID, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=Strict;Secure',
      });
      if (process.env.NODE_ENV !== 'production') console.log('[Observability] GA4 initialized:', GA_ID);
    }

    // Microsoft Clarity (heatmap + session replay, gratuit nelimitat)
    const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
    if (CLARITY_ID && onProductionHost) {
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r); t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', CLARITY_ID);
      if (process.env.NODE_ENV !== 'production') console.log('[Observability] Microsoft Clarity initialized');
    }
  };

  if (typeof window !== 'undefined') {
    if (document.readyState === 'complete') {
      // Pagina deja incarcata — pornim imediat (dar in macrotask)
      setTimeout(initAnalyticsAfterLoad, 0);
    } else {
      window.addEventListener('load', () => {
        // Mic delay pentru a lasa main thread sa termine renderul critic
        setTimeout(initAnalyticsAfterLoad, 100);
      }, { once: true });
    }
  }
}

/** Helper pentru track event manual (ex. signup, scan, upgrade). */
export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
  if (window.clarity) {
    window.clarity('event', eventName);
  }
  // Sentry breadcrumb (poate sa nu fie inca incarcat in primele 1-2s — atunci e no-op)
  if (window.Sentry?.addBreadcrumb) {
    window.Sentry.addBreadcrumb({ category: 'event', message: eventName, data: params, level: 'info' });
  }
}
